// Badge — Presentational badge primitive for the chronological timeline redesign.
//
// Replicates the CloudScape `Badge color=...` visual using our existing
// `designTokens.badgeStyle` plus a color map. Used by PhaseContainer
// (Outbound/Destination/Return phase badges) and TripOverviewCard
// (trip status and trip type badges).
//
// Color map (per design): blue→info, green→success, red→urgent, grey→neutral.
//
// Validates: Requirements 1.4, 1.5, 2.3, 2.4, 2.5

import React from 'react';
import { colors, badgeStyle, fontWeights } from '../../../styles/designTokens';

/**
 * Maps a semantic badge color name to its background color token.
 * Falls back to the neutral (grey) treatment for unknown colors.
 *
 * @param {'blue'|'green'|'red'|'grey'} color
 * @returns {string} background color value
 */
function getBackgroundColor(color) {
  switch (color) {
    case 'blue':
      return colors.info;
    case 'green':
      return colors.success;
    case 'red':
      return colors.urgent;
    case 'grey':
    default:
      return colors.textSecondary;
  }
}

/**
 * @typedef {Object} BadgeProps
 * @property {'blue'|'green'|'red'|'grey'} color - Semantic badge color
 * @property {React.ReactNode} children - Badge label content
 */

/**
 * Badge renders a small colored label pill.
 *
 * @param {BadgeProps} props
 * @returns {React.ReactElement}
 */
export function Badge({ color, children }) {
  return (
    <span
      data-testid="timeline-badge"
      data-color={color}
      style={{
        ...badgeStyle,
        display: 'inline-flex',
        alignItems: 'center',
        backgroundColor: getBackgroundColor(color),
        color: colors.cardHeaderText,
        fontWeight: fontWeights.medium,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

export default Badge;
